/**
 * Persona Hooks
 * Jotai atoms and hooks for Persona management
 */

import { useEffect, useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { invalidateQueries, queryClient } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import { personaService } from '@/services/persona.service';
import { useWebSocket } from '@/hooks/use-websocket';
import { toast } from '@/components/ui/sonner';
import type { Persona, PersonaGeneration } from '@/types';

// ============================================================================
// Query atoms
// ============================================================================

/**
 * While any persona is generating, re-check the list on this interval. The socket event that
 * ends a generation can be missed (a dropped connection, a backend restart), and the sheet
 * would otherwise show "Regenerating" until a manual refresh.
 */
export const GENERATING_POLL_INTERVAL_MS = 10_000;

export function generatingPollInterval(personas: Persona[] | undefined): number | false {
	return personas?.some((persona) => persona.generationStatus === 'generating') ? GENERATING_POLL_INTERVAL_MS : false;
}

export const personasQueryAtom = atomWithQuery((get) => ({
	queryKey: ['personas', buScopeKey(get)],
	queryFn: () => personaService.getPersonas(buScopeParams(get)),
	refetchInterval: (query: { state: { data: Persona[] | undefined } }) => generatingPollInterval(query.state.data)
}));

export const personaByIdQueryAtom = (id: string) =>
	atomWithQuery(() => ({
		queryKey: ['personas', id],
		queryFn: () => personaService.getPersonaById(id),
		enabled: !!id
	}));

export const personaMarkdownQueryAtom = (id: string, version?: string) =>
	atomWithQuery(() => ({
		queryKey: ['personas', id, 'markdown', version ?? 'current'],
		queryFn: () => personaService.getMarkdownContent(id, version),
		enabled: !!id,
		staleTime: 4 * 60 * 1000
	}));

// Keyed under ['personas', id], so the `persona_generated` handler below refetches it after a regenerate.
export const personaChangesQueryAtom = (id: string) =>
	atomWithQuery(() => ({
		queryKey: ['personas', id, 'changes'],
		queryFn: () => personaService.getChanges(id),
		enabled: !!id
	}));

export const personaVersionsQueryAtom = (id: string) =>
	atomWithQuery(() => ({
		queryKey: ['personas', id, 'versions'],
		queryFn: () => personaService.getVersionHistory(id),
		enabled: !!id
	}));

// ============================================================================
// Mutation atoms
// ============================================================================

export const createPersonaMutationAtom = atomWithMutation((get) => ({
	mutationKey: ['personas', 'create'],
	mutationFn: (data: { scope: 'brand' | 'agent'; agentUserIds?: number[] }) =>
		personaService.createPersona(data, buScopeParams(get)),
	onSuccess: () => {
		invalidateQueries(['personas']);
	}
}));

export const refinePersonaMutationAtom = atomWithMutation(() => ({
	mutationKey: ['personas', 'refine'],
	mutationFn: ({ id, feedbackText }: { id: string; feedbackText: string }) =>
		personaService.refinePersona(id, feedbackText),
	onSuccess: (_data, variables) => {
		invalidateQueries(['personas']);
		invalidateQueries(['personas', variables.id]);
	}
}));

export const validateExamplesMutationAtom = atomWithMutation(() => ({
	mutationKey: ['personas', 'validate-examples'],
	mutationFn: ({
		id,
		examples
	}: {
		id: string;
		examples: Array<{ exampleId: string; response: 'looks_good' | 'respond_differently'; alternativeText?: string }>;
	}) => personaService.validateExamples(id, examples),
	onSuccess: (_data, variables) => {
		invalidateQueries(['personas']);
		invalidateQueries(['personas', variables.id]);
	}
}));

export const restoreValidatedPersonaMutationAtom = atomWithMutation(() => ({
	mutationKey: ['personas', 'restore-validated'],
	mutationFn: ({ id }: { id: string }) => personaService.restoreValidatedVersion(id),
	onSuccess: (_data, variables) => {
		invalidateQueries(['personas']);
		invalidateQueries(['personas', variables.id]);
	}
}));

export const updatePersonaGuideMutationAtom = atomWithMutation(() => ({
	mutationKey: ['personas', 'update-guide'],
	// Never replayed: a save whose response was lost would come back as a false conflict.
	retry: false,
	mutationFn: ({ id, markdown, baseVersion }: { id: string; markdown: string; baseVersion: string }) =>
		personaService.updateGuide(id, markdown, baseVersion),
	onSuccess: (_data, variables) => {
		invalidateQueries(['personas']);
		invalidateQueries(['personas', variables.id]);
	}
}));

export const deletePersonaMutationAtom = atomWithMutation(() => ({
	mutationKey: ['personas', 'delete'],
	mutationFn: ({ id }: { id: string }) => personaService.deletePersona(id),
	onSuccess: (_data, variables) => {
		invalidateQueries(['personas']);
		queryClient.removeQueries({ queryKey: ['personas', variables.id] });
	}
}));

// ============================================================================
// Hooks
// ============================================================================

export function usePersonas() {
	const [query] = useAtom(personasQueryAtom);
	return query;
}

export function usePersonaById(id: string) {
	const atom = useMemo(() => personaByIdQueryAtom(id), [id]);
	const [query] = useAtom(atom);
	return query;
}

export function usePersonaMarkdown(id: string, version?: string) {
	const atom = useMemo(() => personaMarkdownQueryAtom(id, version), [id, version]);
	const [query] = useAtom(atom);
	return query;
}

export function usePersonaChanges(id: string) {
	const atom = useMemo(() => personaChangesQueryAtom(id), [id]);
	const [query] = useAtom(atom);
	return query;
}

export function usePersonaVersions(id: string) {
	const atom = useMemo(() => personaVersionsQueryAtom(id), [id]);
	const [query] = useAtom(atom);
	return query;
}

export function useCreatePersona() {
	const [mutation] = useAtom(createPersonaMutationAtom);
	return mutation;
}

export function useRefinePersona() {
	const [mutation] = useAtom(refinePersonaMutationAtom);
	return mutation;
}

export function useValidatePersonaExamples() {
	const [mutation] = useAtom(validateExamplesMutationAtom);
	return mutation;
}

export function useRestoreValidatedPersona() {
	const [mutation] = useAtom(restoreValidatedPersonaMutationAtom);
	return mutation;
}

export function useUpdatePersonaGuide() {
	const [mutation] = useAtom(updatePersonaGuideMutationAtom);
	return mutation;
}

export function useDeletePersona() {
	const [mutation] = useAtom(deletePersonaMutationAtom);
	return mutation;
}

/**
 * Listens for `persona_generated` / `persona_generation_failed` over the socket
 * (backend emits these once `handlePersonaCompleted`/`failPersonaJob` finish) and
 * invalidates the persona queries so the dashboard/card picks up the new
 * generationStatus without polling. Mirrors `useSourceStatusUpdates`.
 */
export function usePersonaStatusUpdates(): void {
	const { setPersonaGeneratedHandler, setPersonaGenerationFailedHandler, isConnected } = useWebSocket();

	useEffect(() => {
		if (!isConnected) return;

		const handleGenerated = (data: { personaId: string }) => {
			invalidateQueries(['personas']);
			invalidateQueries(['personas', data.personaId]);
			toast.success('Persona generated successfully');
		};

		const handleFailed = (data: { personaId?: string; error: string }) => {
			invalidateQueries(['personas']);
			if (data.personaId) invalidateQueries(['personas', data.personaId]);
			toast.error(data.error || 'Persona generation failed');
		};

		setPersonaGeneratedHandler(handleGenerated);
		setPersonaGenerationFailedHandler(handleFailed);

		return () => {
			setPersonaGeneratedHandler(() => {});
			setPersonaGenerationFailedHandler(() => {});
		};
	}, [isConnected, setPersonaGeneratedHandler, setPersonaGenerationFailedHandler]);
}

export type { Persona, PersonaGeneration };
